import { combineReducers } from 'redux';
import _ from 'lodash';
import axios from 'axios';
import humps from 'humps';
import { getFormValues } from 'redux-form';

import {
  WAGE_LEDGER_SEARCH,
  WAGE_LEDGER_PDF,
  WAGE_LEDGER,
  WAGE_LEDGER_EXPORT_ITEM_UPDATE,
  WAGE_LEDGER_PREVIEW_PDF,
  WAGE_LEDGER_DOWNLOAD_PDF,
  WAGE_LEDGER_CREATE_PDF
} from 'src/constants/EndpointUrls';
import createNamedWrapperReducer from 'src/reducers/createNamedWrapperReducer';
import { listReducer, itemReducer } from 'src/reducers/common';
import { newItemFailure } from 'src/reducers/common/itemReducer';
import {
  fetchList,
  fetchListSuccess,
  fetchListFailure,
  getList,
  setList,
  getListPageCount,
  setSelectAllPages,
  getSelectAllPages,
  setExtras,
  getExtras
} from 'src/reducers/common/listReducer';
import { FORM_NAME_WAGE_LEDGER_SEARCH, takeWageLedgersSearchQueries } from 'src/reducers/searchForm';
import { concatParamsToUrl, redirectTo, excludeValuesIfNotDefined } from 'src/utils/Http';
import { setGlobalErrors } from 'src/reducers/global';
import { setStatus, isJobSuccess, isJobIncomplete, fetchJobStatus } from 'src/reducers/JobStatus';
import { MAX_POLLING_INTERVAL } from 'src/constants/Generals';

export const REDUCER_NAME = 'reportWageLedger';
export const clearErrors = () => dispatch => dispatch(newItemFailure(REDUCER_NAME, []));
export const setErrors = errors => dispatch => dispatch(newItemFailure(REDUCER_NAME, errors));
export const getWageLedgerList = state => getList(REDUCER_NAME, state);
export const setWageLedgerList = state => setList(REDUCER_NAME, state);
export const isAllWageLedgerChecked = state => _.every(getWageLedgerList(state) || [], { checked: true });
export const getWageLedgerQueries = state =>
  takeWageLedgersSearchQueries(getFormValues(FORM_NAME_WAGE_LEDGER_SEARCH)(state) || {});
export const checkedEmployee = state => _.filter(getWageLedgerList(state) || [], item => item.checked);

export const getDownloadParams = state => {
  const checkedEmp = checkedEmployee(state);
  if (_.isEmpty(checkedEmp)) {
    return {};
  }
  if (getSelectAllPages(REDUCER_NAME, state)) {
    return { ...getWageLedgerQueries(state), exportAll: 1 };
  }
  const year = _.get(getWageLedgerQueries(state), 'year');
  return { employeeIds: checkedEmp.map(item => item.id).join(','), year };
};

export const getPdfPreviewUrl = state => {
  const checkedEmp = checkedEmployee(state);
  if (!_.isEmpty(checkedEmp)) {
    const year = _.get(getWageLedgerQueries(state), 'year');
    return `${WAGE_LEDGER_PDF}?employee_ids=${checkedEmp[0].id}&year=${year}`;
  }

  return undefined;
};
// Async Action Creators
export const fetchReportWageLedger = queries => async dispatch => {
  try {
    dispatch(fetchList(REDUCER_NAME));
    const excluedQueries = excludeValuesIfNotDefined(queries);
    const response = await axios.get(WAGE_LEDGER_SEARCH, { params: humps.decamelizeKeys(excluedQueries) });

    const { totalCount = 0, displayFrom = 0, displayTo = 0 } = response.data.payload;
    const payload = {
      data: response.data.payload.employees,
      pageCount: response.data.payload.totalPages,
      totalCount,
      displayFrom,
      displayTo
    };
    dispatch(fetchListSuccess(REDUCER_NAME, payload));
    dispatch(setExtras(REDUCER_NAME, { uniqString: response.data.payload.uniqString }));
  } catch (exception) {
    dispatch(fetchListFailure(REDUCER_NAME, exception.response.data.errors.messages));
  }
};

export const changeChecked = id => (dispatch, getState) => {
  const pageCount = getListPageCount(REDUCER_NAME, getState());
  const wageList = getWageLedgerList(getState()).map(
    item => (item.id === id ? { ...item, checked: !item.checked } : item)
  );

  dispatch(setWageLedgerList({ data: wageList, pageCount }));
  dispatch(setSelectAllPages(REDUCER_NAME, { isSelectAllPages: false }));
};

export const changeCheckedCurrentPage = newValue => (dispatch, getState) => {
  const pageCount = getListPageCount(REDUCER_NAME, getState());
  const wageList = getWageLedgerList(getState()).map(item => ({ ...item, checked: newValue }));

  dispatch(setWageLedgerList({ data: wageList, pageCount }));
  dispatch(setSelectAllPages(REDUCER_NAME, { isSelectAllPages: false }));
};

export const changeCheckedAllPage = newValue => dispatch => {
  dispatch(changeCheckedCurrentPage(newValue));
  dispatch(setSelectAllPages(REDUCER_NAME, { isSelectAllPages: true, selectAllPageValue: newValue }));
};

export const saveExportItems = data => async dispatch => {
  try {
    if (_.isEmpty(_.get(data, 'exportItems'))) {
      dispatch(setGlobalErrors('項目名を1つ以上選択してください。'));
      return;
    }
    const url = concatParamsToUrl(WAGE_LEDGER_EXPORT_ITEM_UPDATE, { data });
    await axios.post(url, data);

    redirectTo(WAGE_LEDGER);
  } catch (exception) {
    dispatch(setGlobalErrors(exception.response.data.errors.messages));
  }
};
export default combineReducers({
  list: createNamedWrapperReducer(listReducer, REDUCER_NAME),
  item: createNamedWrapperReducer(itemReducer, REDUCER_NAME)
});

export const createPdf = (params, hideModal) => async (dispatch, getState) => {
  try {
    const state = getState();
    const jobStatusHash = state.JobStatus;
    const response = await axios.post(WAGE_LEDGER_CREATE_PDF, params);
    jobStatusHash.exportWageLedgerJob = response.data.payload.jobStatus;
    dispatch(setStatus(jobStatusHash));
    dispatch(setExtras(REDUCER_NAME, { isCreating: true }));
    dispatch(setExtras(REDUCER_NAME, { isCreated: false }));
    hideModal();
  } catch (e) {
    dispatch(setGlobalErrors(e.response.data.errors.messages));
  }
};

export const fetchExportWageLedgerStatus = (interval, year, uniqueString) => async (dispatch, getState) => {
  try {
    if (year === undefined || uniqueString === undefined) {
      return;
    }
    const params = {
      job_name: 'exportWageLedgerJob',
      search_params: {
        year,
        unique_string: uniqueString
      }
    };
    await dispatch(fetchJobStatus(params));
    if (isJobIncomplete(getState().JobStatus.exportWageLedgerJob.status) === true) {
      const nextInterval = interval < MAX_POLLING_INTERVAL ? interval * 2 : interval;
      setTimeout(() => {
        dispatch(fetchExportWageLedgerStatus(nextInterval, year, uniqueString));
      }, nextInterval);
      dispatch(setExtras(REDUCER_NAME, { isCreating: true }));
      dispatch(setExtras(REDUCER_NAME, { isCreated: false }));
    } else if (isJobSuccess(getState().JobStatus.exportWageLedgerJob.status)) {
      dispatch(setExtras(REDUCER_NAME, { isCreated: true }));
      dispatch(setExtras(REDUCER_NAME, { isCreating: false }));
    } else {
      dispatch(setExtras(REDUCER_NAME, { isCreating: false }));
      dispatch(setExtras(REDUCER_NAME, { isCreated: false }));
    }
  } catch (exception) {
    dispatch(setExtras(REDUCER_NAME, { isCreated: false }));
    dispatch(setExtras(REDUCER_NAME, { isCreating: false }));
    dispatch(setGlobalErrors('システムエラーが発生しました'));
  }
};

export const fetchDownloadWageLedger = year => async dispatch => {
  try {
    const url = `${WAGE_LEDGER_DOWNLOAD_PDF}?year=${year}`;
    redirectTo(url);
  } catch (exception) {
    dispatch(setGlobalErrors('システムエラーが発生しました'));
  }
};

export const fetchPreviewWageLedger = year => async dispatch => {
  try {
    const url = `${WAGE_LEDGER_PREVIEW_PDF}?year=${year}`;
    const w = window.open(url, '', 'toolbar=0,menubar=0,location=0');
    if (_.isFunction(w.addEventListener)) {
      w.addEventListener('load', () => {
        w.print();
      });
    }
  } catch (exception) {
    dispatch(setGlobalErrors('システムエラーが発生しました'));
  }
};

export const getIsCreating = state => _.get(getExtras(REDUCER_NAME, state), 'isCreating');
export const getIsCreated = state => _.get(getExtras(REDUCER_NAME, state), 'isCreated');
